import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  CrewChangeDetailContainerStyled,
  CrewChangeDetailHeaderStyled,
  CrewChangeDetailSubHeaderStyled,
  CrewChangeDetailContentStyled,
  CrewChangeDetailFooterStyled,
  FooterButtonStyled,
  CrewChangeDetailContentLayoutStyled,
} from './styled';
import { Box, Spinner, SpinnerSize, Text, ButtonAppearance, AvatarSize } from '@bechtel/gds';
import { NetworkStatus } from '@apollo/client';
import {
  CrewChangeRequest,
  CrewChangeRequestStatus,
  CrewChangeRequestMoveType,
  CrewChangeRequestStatusCode,
} from '@bechtel/types';
import { get, minBy, maxBy, filter, map, concat } from 'lodash';
import { CrewTree, CrewMemberExt, ErrorRetry, useName, useDate, TextWithEllipsis } from '@bechtel/client-common';
import { useCrewMember } from '../hooks/use-crew-member';
import { CrewChangeActions } from '../types';
import { useCrewChangeRequestsByIds } from '../hooks/use-crew-change';
import { parseISO } from 'date-fns';
import { useCrewChangePermissions } from '../hooks/use-crew-change-permission';
import { t, Trans } from '@lingui/macro';
//@ts-ignore needed for declaration generation
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/crew-change.svg';

interface RouteParams {
  id: string;
}

export interface CrewChangeDetailProps {
  onAction: (crewChangeRequestId: string, action: CrewChangeActions) => void;
  actionedCrewChangeRequestId: string;
  approveProgress?: boolean;
  declineProgress?: boolean;
  cancelProgress?: boolean;
}

export const CrewChangeDetail: FC<CrewChangeDetailProps> = ({
  onAction,
  actionedCrewChangeRequestId,
  approveProgress,
  declineProgress,
  cancelProgress,
}) => {
  const { id } = useParams<RouteParams>();
  const { canApproveDeclineRequest, canCancelRequest, canApproveAllRequest } = useCrewChangePermissions();
  const { getFullName } = useName();
  const { formatRelativeInWords } = useDate();
  const { getCrewMemberExt } = useCrewMember();
  const decodedId = id ? decodeURIComponent(id) : '';
  const [loadCrewChangeRequestByIds, { loading, data, error, refetch, networkStatus }] = useCrewChangeRequestsByIds();

  useEffect(() => {
    if (!decodedId) return;
    loadCrewChangeRequestByIds({
      variables: {
        ids: [decodedId],
      },
    });
  }, [decodedId]);

  const crewChangeRequest: CrewChangeRequest | undefined = get(data, 'crewChangeRequestsByIds[0]');

  if (!decodedId)
    return (
      <CrewChangeDetailContainerStyled>
        <Box
          flexDirection={{ sm: 'column' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          style={{ height: '100%' }}
        >
          <PackageIcon />
          <Box mt={{ sm: '700' }} px={{ sm: '600' }} data-testid="no-crew-change-request">
            <Text family="graphik" color="grey.600">
              <Trans>Select a crew change request</Trans>
            </Text>
          </Box>
        </Box>
      </CrewChangeDetailContainerStyled>
    );

  if (networkStatus === NetworkStatus.refetch || loading)
    return (
      <CrewChangeDetailContainerStyled data-testid="loading">
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} style={{ height: '100%', width: '100%' }}>
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      </CrewChangeDetailContainerStyled>
    );

  if (error)
    return (
      <CrewChangeDetailContainerStyled>
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          style={{ height: '100%', width: '100%' }}
        >
          <ErrorRetry refetch={refetch}>
            <Trans>
              There was a problem loading crew change request detail. Please click the button below to try again.
            </Trans>
          </ErrorRetry>
        </Box>
      </CrewChangeDetailContainerStyled>
    );

  if (crewChangeRequest) {
    const {
      moveType,
      affectedCrewMembers,
      originCrew,
      statusHistory,
      destinationCrew,
      currentStatus,
      comments,
    } = crewChangeRequest;

    const createdInfo: CrewChangeRequestStatus = minBy(statusHistory, 'createdAt') as CrewChangeRequestStatus;
    const canApproveDecline =
      canApproveDeclineRequest(
        concat(originCrew.foreman, originCrew.generalForeman, originCrew.superintendent),
        concat(destinationCrew.foreman, destinationCrew.generalForeman, destinationCrew.superintendent),
        createdInfo.createdBy
      ) || canApproveAllRequest;
    const canCancel = canCancelRequest(createdInfo.createdBy);
    const actionInfo: CrewChangeRequestStatus = maxBy(statusHistory, 'createdAt') as CrewChangeRequestStatus;
    const filteredCrewMemebers = filter(affectedCrewMembers, (acm) => acm.id != originCrew.foreman.id);
    const sourceAffectedCrewMembers: CrewMemberExt[] =
      moveType == CrewChangeRequestMoveType.CRAFT
        ? map(filteredCrewMemebers, (affectedCrewMember) => {
            return {
              ...getCrewMemberExt(affectedCrewMember),
              isAffected: true,
              showArrow: true,
              hideTreeLine: true,
              avatarSize: AvatarSize.MEDIUM,
            };
          })
        : map(filteredCrewMemebers, (affectedCrewMember) => {
            return {
              ...getCrewMemberExt(affectedCrewMember),
              isAffected: true,
              hideTreeLine: true,
              showArrow: false,
              avatarSize: AvatarSize.MEDIUM,
            };
          });

    const destAffectedCrewMembers: CrewMemberExt[] = map(sourceAffectedCrewMembers, (sourceAffectedCrewMember) => {
      return {
        ...sourceAffectedCrewMember,
        isAffected: false,
        showArrow: false,
      };
    });

    const sourceCrewTreeMembers: CrewMemberExt[] = concat(
      [],
      getCrewMemberExt(originCrew.superintendent),
      getCrewMemberExt(originCrew.generalForeman),
      moveType == CrewChangeRequestMoveType.CRAFT
        ? getCrewMemberExt(originCrew.foreman)
        : { ...getCrewMemberExt(originCrew.foreman), isAffected: true, showArrow: true },
      sourceAffectedCrewMembers
    );

    const destCrewTreeMembers: CrewMemberExt[] = concat(
      [],
      getCrewMemberExt(destinationCrew.superintendent),
      getCrewMemberExt(destinationCrew.generalForeman),
      getCrewMemberExt(destinationCrew.foreman),
      destAffectedCrewMembers
    );

    return (
      <CrewChangeDetailContainerStyled>
        <CrewChangeDetailContentLayoutStyled>
          <CrewChangeDetailHeaderStyled>
            <Box
              alignItems={{ sm: 'center' }}
              pl={{ sm: '500' }}
              style={{ width: '100%', height: '100%' }}
              data-testid="crew-change-detail-header"
            >
              <Text size="600" weight="600" family="graphik" color="white">
                {moveType == CrewChangeRequestMoveType.CREW
                  ? t`Move Entire ${originCrew.crewNumber} Crew (${affectedCrewMembers.length})`
                  : concat(t`Move Craft`, ` (${affectedCrewMembers.length})`)}
              </Text>
            </Box>
          </CrewChangeDetailHeaderStyled>
          <CrewChangeDetailSubHeaderStyled>
            <Box
              flexDirection={{ sm: 'column' }}
              justifyContent={{ sm: 'center' }}
              alignItems={{ sm: 'flex-start' }}
              pl={{ sm: '500' }}
              style={{ width: '100%', height: '100%' }}
            >
              <Box>
                <Text size="500" weight="600" family="graphik" color="black">
                  <Trans>Requested by:</Trans>
                </Text>
                <Text size="500" family="graphik" color="grey.1000" data-testid="requested-by">
                  &nbsp;{getFullName(createdInfo?.createdBy.firstName, createdInfo?.createdBy.lastName)}
                  &nbsp;{formatRelativeInWords(parseISO(createdInfo?.createdAt))}
                </Text>
              </Box>
              {comments && (
                <Box
                  pt={currentStatus == CrewChangeRequestStatusCode.PENDING_APPROVAL ? { sm: '500' } : { sm: '400' }}
                  style={{ width: '500px' }}
                >
                  <Text size="500" weight="600" family="graphik" color="black" style={{ lineHeight: 'normal' }}>
                    <Trans>Comment:</Trans>
                  </Text>
                  <TextWithEllipsis
                    size="500"
                    family="graphik"
                    color="grey.1000"
                    title={comments}
                    data-testid="comments"
                  >
                    &nbsp;{comments}
                  </TextWithEllipsis>
                </Box>
              )}
              {currentStatus != CrewChangeRequestStatusCode.PENDING_APPROVAL && (
                <Box pt={comments ? { sm: '400' } : { sm: '500' }} data-testid="action-info">
                  <Text size="500" weight="600" family="graphik" color="black">
                    {actionInfo.status == CrewChangeRequestStatusCode.APPROVED
                      ? t`Approved by:`
                      : actionInfo.status == CrewChangeRequestStatusCode.DECLINED
                      ? t`Declined by:`
                      : t`Cancelled by:`}
                  </Text>
                  <Text size="500" family="graphik" color="grey.1000">
                    &nbsp;{getFullName(actionInfo?.createdBy.firstName, actionInfo?.createdBy.lastName)}
                    &nbsp;{formatRelativeInWords(parseISO(actionInfo?.createdAt))}
                  </Text>
                </Box>
              )}
            </Box>
          </CrewChangeDetailSubHeaderStyled>
          <CrewChangeDetailContentStyled>
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} mt={{ sm: '800' }} mb={{ sm: '400' }}>
              <CrewTree crewMembers={sourceCrewTreeMembers} showAffectedCrewMembers={true} label={t`Original Crew:`} />
              <CrewTree
                crewMembers={destCrewTreeMembers}
                showAffectedCrewMembers={false}
                label={t`Destination Crew:`}
              />
            </Box>
          </CrewChangeDetailContentStyled>
          <CrewChangeDetailFooterStyled>
            <Box
              justifyContent={{ sm: 'flex-end' }}
              alignItems={{ sm: 'center' }}
              pt={{ sm: '100' }}
              pr={{ sm: '500' }}
              style={{ height: '100%' }}
            >
              {currentStatus == CrewChangeRequestStatusCode.PENDING_APPROVAL && (
                <>
                  {canApproveDecline && (
                    <>
                      <Box mr={{ sm: '400' }}>
                        <FooterButtonStyled
                          appearance={ButtonAppearance.SECONDARY}
                          disabled={actionedCrewChangeRequestId != ''}
                          onClick={() => {
                            onAction(id, CrewChangeActions.DECLINE);
                          }}
                          data-testid="decline-btn"
                        >
                          {declineProgress && actionedCrewChangeRequestId != '' && id == actionedCrewChangeRequestId ? (
                            <Spinner />
                          ) : (
                            <Trans>Decline</Trans>
                          )}
                        </FooterButtonStyled>
                      </Box>
                      <Box>
                        <FooterButtonStyled
                          appearance={ButtonAppearance.SECONDARY}
                          disabled={actionedCrewChangeRequestId != ''}
                          onClick={() => {
                            onAction(id, CrewChangeActions.APPROVE);
                          }}
                          data-testid="approve-btn"
                        >
                          {approveProgress && actionedCrewChangeRequestId != '' && id == actionedCrewChangeRequestId ? (
                            <Spinner />
                          ) : (
                            <Trans>Approve</Trans>
                          )}
                        </FooterButtonStyled>
                      </Box>
                    </>
                  )}
                  {canCancel && (
                    <Box ml={{ sm: '400' }}>
                      <FooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={actionedCrewChangeRequestId != ''}
                        onClick={() => {
                          onAction(id, CrewChangeActions.CANCEL);
                        }}
                      >
                        {cancelProgress && actionedCrewChangeRequestId != '' && id == actionedCrewChangeRequestId ? (
                          <Spinner />
                        ) : (
                          <Trans>Cancel Request</Trans>
                        )}
                      </FooterButtonStyled>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </CrewChangeDetailFooterStyled>
        </CrewChangeDetailContentLayoutStyled>
      </CrewChangeDetailContainerStyled>
    );
  }
  return null;
};
